/**
 * Screens 00 «Создать аккаунт», 01 «Вход» and 01a «Забыли пароль?»
 * (salon-mockup.html signup / login / forgot). Before sign-in the language is
 * the device's: Kyrgyz by default, one tap to Russian (D-24).
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { useAuth } from '../auth';
import { ApiError, api } from '../core/api';
import { Mark } from '../components/icons';
import { Sheet } from '../components/Shell';
import { errorText, setLocale } from '../i18n';
import type { Locale } from '../types';

function LangSwitch() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Locale;
  // A language is named in its own language, so these are not translated.
  return (
    <div className="langsw">
      {(['ky', 'ru'] as Locale[]).map((l) => (
        <span
          key={l}
          role="button"
          tabIndex={0}
          aria-pressed={lang === l}
          className={`lng${lang === l ? ' on' : ''}`}
          onClick={() => void setLocale(l)}
          onKeyDown={(e) => e.key === 'Enter' && void setLocale(l)}
        >
          {t(`lang.${l}`)}
        </span>
      ))}
    </div>
  );
}

export function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="app">
      <div className="shell">
        <div className="body">{children}</div>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="logo">
      <Mark />
    </div>
  );
}

function Title({ title, sub }: { title: string; sub: string }) {
  return (
    <>
      <h2 style={{ fontSize: 22, letterSpacing: '-.5px', margin: '0 0 6px' }}>{title}</h2>
      <p className="sm" style={{ margin: '0 0 20px' }}>
        {sub}
      </p>
    </>
  );
}

function Field({
  label,
  hint,
  error,
  ...input
}: { label: string; hint?: string; error?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className="fld">
      <label>
        {label}
        <input className="inp" {...input} />
      </label>
      {error ? (
        <span className="field-error">{error}</span>
      ) : (
        hint && (
          <span className="xs" style={{ display: 'block', marginTop: 5 }}>
            {hint}
          </span>
        )
      )}
    </div>
  );
}

function useAfterSignIn() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signedIn } = useAuth();
  return (user: Parameters<typeof signedIn>[0]) => {
    signedIn(user);
    const next = params.get('next');
    navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/today', { replace: true });
  };
}

/** 01 «Вход», with 01a «Забыли пароль?» as a sheet over it. */
export function LoginScreen() {
  const { t } = useTranslation();
  const done = useAfterSignIn();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      done(await api.login(email, password));
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame>
      <div className="login">
        <form className="loginbox" onSubmit={submit} noValidate>
          <LangSwitch />
          <Logo />
          <Title title={t('login.title')} sub={t('auth.cabinet')} />
          {error && <div className="warnbox form-error" role="alert">{error}</div>}
          <Field label={t('auth.email')} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field label={t('auth.password')} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button className="btn pri blk" type="submit" disabled={busy} style={{ marginTop: 6 }}>
            {t('login.submit')}
          </button>
          <a className="linkbtn" role="button" tabIndex={0} onClick={() => setForgot(true)}>
            {t('login.forgot')}
          </a>
          <Link className="linkbtn" to="/register" style={{ marginTop: 0 }}>
            {t('login.toSignup')}
          </Link>
        </form>
      </div>
      {forgot && (
        <Sheet onClose={() => setForgot(false)}>
          <h3>{t('forgot.title')}</h3>
          <p className="lead">{t('forgot.lead')}</p>
          <div style={{ display: 'grid', gap: 10 }}>
            {/* Support contacts are not known yet: shown, disabled (D-40, doc 10 §6). */}
            {(['wa', 'tg'] as const).map((ch) => (
              <button key={ch} type="button" className="btn blk" disabled>
                <span className={`pill ${ch}`}>{ch === 'wa' ? 'WhatsApp' : 'Telegram'}</span>
                {t('forgot.write')}
                <span className="soon">{t('app.soon')}</span>
              </button>
            ))}
          </div>
          <p className="xs" style={{ margin: '14px 0 0', textAlign: 'center' }}>
            {t('forgot.note')}
          </p>
          <button type="button" className="btn blk" style={{ marginTop: 14 }} onClick={() => setForgot(false)}>
            {t('forgot.back')}
          </button>
        </Sheet>
      )}
    </AuthFrame>
  );
}

/** 00 «Создать аккаунт» (D-22): four fields, no salon name. */
export function SignupScreen() {
  const { t, i18n } = useTranslation();
  const done = useAfterSignIn();
  const [form, setForm] = useState({ name: '', email: '', password: '', repeat: '' });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    // The repeat box never leaves the browser (D-22).
    const local: Record<string, string> = {};
    if (form.password.length < 8) local.password = t('signup.passwordHint');
    if (form.repeat !== form.password) local.repeat = t('signup.mismatch');
    setFieldErrors(local);
    setError(null);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      done(
        await api.register({
          name: form.name,
          email: form.email,
          password: form.password,
          locale: i18n.language as Locale,
        }),
      );
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'fallback'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame>
      <div className="login">
        <form className="loginbox" onSubmit={submit} noValidate>
          <LangSwitch />
          <Logo />
          <Title title={t('signup.submit')} sub={t('auth.cabinet')} />
          {error && <div className="warnbox form-error" role="alert">{error}</div>}
          <Field label={t('signup.name')} hint={t('signup.nameHint')} autoComplete="name" value={form.name} onChange={set('name')} />
          <Field label={t('auth.email')} hint={t('signup.emailHint')} type="email" autoComplete="username" value={form.email} onChange={set('email')} />
          <Field
            label={t('auth.password')}
            hint={t('signup.passwordHint')}
            error={fieldErrors.password}
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={set('password')}
          />
          <Field label={t('signup.repeat')} error={fieldErrors.repeat} type="password" autoComplete="new-password" value={form.repeat} onChange={set('repeat')} />
          <button className="btn pri blk" type="submit" disabled={busy} style={{ marginTop: 6 }}>
            {t('signup.submit')}
          </button>
          <Link className="linkbtn" to="/login">
            {t('signup.toLogin')}
          </Link>
        </form>
      </div>
    </AuthFrame>
  );
}
